import {FC, useState, useEffect} from 'react';
import {useLocation, Link} from 'react-router-dom';
import DashboardLayout from "@/components/layouts/Dashboard";
import Card from "@/components/Card";
import {
  CheckCircleIcon,
  XCircleIcon,
  HomeIcon,
  CreditCardIcon,
  AlertTriangleIcon,
  ClockIcon,
  RefreshCw,
  Rocket
} from "lucide-react";
import {apiSessionStatus, SessionStatusResponse} from '@/services/PaymentService';
import {Button} from "@/components/ui/button";
import {useSubscriptionCheck} from '@/hooks/useSubscriptionCheck';

interface PaymentResultPageProps {
  mode?: 'success' | 'cancel';
}

const PaymentResultPage: FC<PaymentResultPageProps> = ({ mode }) => {
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatusResponse | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [returnToProjectId, setReturnToProjectId] = useState<string | null>(null);
  const { status: subscriptionStatus, coins, hasValidSubscriptionOrCredits, refetch: refetchSubscription } = useSubscriptionCheck();

  console.log('[Payment] Page loaded with mode:', mode, 'path:', location.pathname);
  
  const fetchSessionStatus = async (sessionId: string) => {
    try {
      setIsRefreshing(true);
      const response = await apiSessionStatus(sessionId);
      console.log('[Payment] Session status response:', response.data);
      setSessionStatus(response.data);
      setError(null);

      // If payment successful, refetch subscription data to update UI
      if (response.data.status === 'SUCCESS') {
        console.log('[Payment] Payment successful, refetching subscription...');
        await refetchSubscription();
      }
    } catch (err: any) {
      console.error('[Payment] Failed to fetch session status:', err);
      setError("Unable to verify payment status. Please try again or contact support.");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };
  
  useEffect(() => {
    const checkSessionStatus = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get('session_id');

        // Retrieve stored project ID
        const storedProjectId = localStorage.getItem('returnToProjectId');
        if (storedProjectId) {
          setReturnToProjectId(storedProjectId);
          console.log('[Payment] Found return project ID:', storedProjectId);
        }

        if (!sessionId) {
          console.log('[Payment] No session ID found, mode:', mode);

          // If mode is 'success', we're on /payment/success route - assume success
          if (mode === 'success') {
            console.log('[Payment] Mode is success, waiting for subscription data...');
            // Wait for subscription data to load
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Refetch subscription to get latest status
            await refetchSubscription();
            await new Promise(resolve => setTimeout(resolve, 1000));

            console.log('[Payment] After refetch - Subscription status:', subscriptionStatus, 'Coins:', coins);

            // Show success regardless (user is on success page)
            setSessionStatus({ status: 'SUCCESS' } as SessionStatusResponse);
            setLoading(false);
            return;
          }

          // If mode is not success, check subscription status
          console.log('[Payment] No session ID and mode is not success, checking subscription status...');
          await new Promise(resolve => setTimeout(resolve, 1500));

          // Refetch subscription to get latest status
          await refetchSubscription();
          await new Promise(resolve => setTimeout(resolve, 1000));

          console.log('[Payment] Subscription status:', subscriptionStatus, 'Coins:', coins);

          // Check if user has valid subscription or credits
          if (hasValidSubscriptionOrCredits()) {
            console.log('[Payment] Valid subscription/credits detected, showing success');
            setSessionStatus({ status: 'SUCCESS' } as SessionStatusResponse);
          } else {
            console.log('[Payment] No valid subscription/credits found');
            setError("No session ID found. Unable to verify payment status.");
          }
          setLoading(false);
          return;
        }

        console.log('[Payment] Session ID found:', sessionId);
        await fetchSessionStatus(sessionId);
      } catch (err: any) {
        console.error('[Payment] Error:', err);
        setError("An unexpected error occurred. Please try again or contact support.");
        setLoading(false);
      }
    };

    checkSessionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, mode]);
  
  const handleRefresh = () => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('session_id');
    
    if (sessionId) {
      fetchSessionStatus(sessionId);
    }
  };
  
  // Render payment result content
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
          <p className="text-gray-600 text-lg">
            Checking payment status...
          </p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="bg-red-100 rounded-full p-4 mb-4">
            <AlertTriangleIcon className="h-16 w-16 text-red-500"/>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Payment Verification Error
          </h2>
          <div
            className="inline-block px-3 py-1 text-sm bg-red-50 text-red-500 border border-red-200 rounded-full mb-4">Error
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-lg">
            <p className="text-red-800 text-center">{error}</p>
          </div>
          
          <div className="flex items-center justify-center gap-8">
            <Button
              variant="default"
              onClick={handleRefresh}
              className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              icon={<RefreshCw className="h-4 w-4 mr-2"/>}
              loading={isRefreshing}
            >
              Refresh
            </Button>
            <div className="h-6 w-px bg-gray-300"></div>
            <Link
              to="/dashboard"
              className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <HomeIcon className="h-4 w-4 mr-2"/>
              Dashboard
            </Link>
            <div className="h-6 w-px bg-gray-300"></div>
            <Link
              to="/pricing/plans"
              className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <CreditCardIcon className="h-4 w-4 mr-2"/>
              View Plans
            </Link>
          </div>
        </div>
      );
    }
    
    if (!sessionStatus) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="bg-red-100 rounded-full p-4 mb-4">
            <AlertTriangleIcon className="h-16 w-16 text-red-500"/>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Payment Information</h2>
          <div
            className="inline-block px-3 py-1 text-sm bg-red-50 text-red-500 border border-red-200 rounded-full mb-4">Error
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-lg">
            <p className="text-red-800 text-center">
              We couldn't find any payment information. Please try again or contact support.
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-8">
            <Link
              to="/dashboard"
              className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <HomeIcon className="h-4 w-4 mr-2"/>
              Dashboard
            </Link>
            <div className="h-6 w-px bg-gray-300"></div>
            <Link
              to="/pricing/plans"
              className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <CreditCardIcon className="h-4 w-4 mr-2"/>
              View Plans
            </Link>
          </div>
        </div>
      );
    }
    
    // Handle each status type
    switch (sessionStatus.status) {
      case 'SUCCESS':
        return (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="bg-green-100 rounded-full p-4 mb-4">
              <CheckCircleIcon className="h-16 w-16 text-green-500"/>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Successful</h2>
            <div
              className="inline-block px-3 py-1 text-sm bg-green-50 text-green-500 border border-green-200 rounded-full mb-4">Success
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 max-w-lg">
              <p className="text-green-800 text-center mb-3">
                Your payment has been processed successfully. Thank you for your purchase!
              </p>
              {(coins > 0 || subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'ACTIVE_UNTIL_PERIOD_END') && (
                <div className="flex items-center justify-center gap-4 text-sm">
                  {coins > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="font-semibold text-green-700">Credits:</span>
                      <span className="text-green-900 font-bold">{coins.toLocaleString()}</span>
                    </div>
                  )}
                  {(subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'ACTIVE_UNTIL_PERIOD_END') && (
                    <div className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                      SUBSCRIPTION ACTIVE
                    </div>
                  )}
                </div>
              )}
            </div>

            {returnToProjectId ? (
              <div className="flex flex-col items-center gap-4">
                <Link
                  to={`/architecture/preview/${returnToProjectId}`}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => {
                    // Clear the stored project ID after navigation
                    localStorage.removeItem('returnToProjectId');
                  }}
                >
                  <Rocket className="h-6 w-6"/>
                  Continue to Development
                </Link>
                <Link
                  to="/dashboard"
                  className="flex items-center text-gray-600 hover:text-gray-800 font-medium text-sm"
                >
                  <HomeIcon className="h-4 w-4 mr-2"/>
                  Or go to Dashboard
                </Link>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <Link
                  to="/dashboard"
                  className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <HomeIcon className="h-4 w-4 mr-2"/>
                  Go to Dashboard
                </Link>
              </div>
            )}
          </div>
        );
      
      case 'PENDING':
        return (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="bg-yellow-100 rounded-full p-4 mb-4">
              <ClockIcon className="h-16 w-16 text-yellow-500"/>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Processing</h2>
            <div
              className="inline-block px-3 py-1 text-sm bg-yellow-50 text-yellow-600 border border-yellow-200 rounded-full mb-4">Pending
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 max-w-lg">
              <p className="text-yellow-800 text-center">
                Your payment is currently being processed. This may take a moment to complete.
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-8">
              <Button
                variant="default"
                onClick={handleRefresh}
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
                icon={<RefreshCw className="h-4 w-4 mr-2"/>}
                loading={isRefreshing}
              >
                Check Status
              </Button>
              <div className="h-6 w-px bg-gray-300"></div>
              <Link
                to="/dashboard"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <HomeIcon className="h-4 w-4 mr-2"/>
                Dashboard
              </Link>
            </div>
          </div>
        );
      
      case 'FAILED':
        return (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="bg-red-100 rounded-full p-4 mb-4">
              <XCircleIcon className="h-16 w-16 text-red-500"/>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Failed</h2>
            <div
              className="inline-block px-3 py-1 text-sm bg-red-50 text-red-500 border border-red-200 rounded-full mb-4">Failed
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 max-w-lg">
              <p className="text-amber-800 text-center">
                Your payment couldn't be processed. This could be due to insufficient funds,
                expired card, or other payment issues.
              </p>
              <p className="text-amber-800 text-center mt-2">
                Please try again with a different payment method.
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-8">
              <Link
                to="/dashboard"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <HomeIcon className="h-4 w-4 mr-2"/>
                Dashboard
              </Link>
              <div className="h-6 w-px bg-gray-300"></div>
              <Link
                to="/pricing/plans"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <CreditCardIcon className="h-4 w-4 mr-2"/>
                Try Again
              </Link>
            </div>
          </div>
        );
      
      case 'EXPIRED':
        return (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="bg-gray-100 rounded-full p-4 mb-4">
              <ClockIcon className="h-16 w-16 text-gray-500"/>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Session Expired</h2>
            <div
              className="inline-block px-3 py-1 text-sm bg-gray-100 text-gray-600 border border-gray-200 rounded-full mb-4">Expired
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-8 max-w-lg">
              <p className="text-gray-800 text-center">
                This payment session has expired. Please start a new payment process.
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-8">
              <Link
                to="/dashboard"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <HomeIcon className="h-4 w-4 mr-2"/>
                Dashboard
              </Link>
              <div className="h-6 w-px bg-gray-300"></div>
              <Link
                to="/pricing/plans"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <CreditCardIcon className="h-4 w-4 mr-2"/>
                Start New Payment
              </Link>
            </div>
          </div>
        );
      
      case 'NOT_FOUND':
      default:
        return (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="bg-gray-100 rounded-full p-4 mb-4">
              <AlertTriangleIcon className="h-16 w-16 text-gray-500"/>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Not Found</h2>
            <div
              className="inline-block px-3 py-1 text-sm bg-gray-100 text-gray-600 border border-gray-200 rounded-full mb-4">Not
              Found
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-8 max-w-lg">
              <p className="text-gray-800 text-center">
                We couldn't find any record of this payment session. It may have been canceled or never existed.
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-8">
              <Link
                to="/dashboard"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <HomeIcon className="h-4 w-4 mr-2"/>
                Dashboard
              </Link>
              <div className="h-6 w-px bg-gray-300"></div>
              <Link
                to="/pricing/plans"
                className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <CreditCardIcon className="h-4 w-4 mr-2"/>
                View Plans
              </Link>
            </div>
          </div>
        );
    }
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl m-auto flex flex-col items-stretch gap-5 h-full w-full grow">
        <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="full" grow>
          <div className="my-8 mx-4">
            {renderContent()}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PaymentResultPage;